import {
  DatepickerFormField,
  SelectFormField,
  TextFormField,
} from 'components/DynamicFormFields';
import { PatientGuideStatus } from '@common/types';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { isNil } from 'lodash';

interface EditProgramFields {
  tierOptions: string[];
  alignmentOption: PatientGuideStatus;
  programStatusOptions: PatientGuideStatus[];
  unalignmentReasons: string[];
  ineligibilityReasons: string[];
  currentStatus?: PatientGuideStatus;
  programName: string;
  disabled?: boolean;
}

export const EditProgramFields = ({
  tierOptions,
  alignmentOption,
  programStatusOptions,
  unalignmentReasons,
  ineligibilityReasons,
  currentStatus,
  programName,
  disabled = false,
}: EditProgramFields) => {
  const { control, watch } = useFormContext();
  const respite_eligible = watch('respite_eligible');
  const isStatusChanged = currentStatus !== alignmentOption;

  const isNewStatusAligned = alignmentOption === 'Aligned' && isStatusChanged;
  const isNewStatusRejected =
    (alignmentOption === 'Rejected' ||
      alignmentOption === 'Resubmission rejected') &&
    isStatusChanged;
  const isNewStatusUnaligned =
    (alignmentOption === 'Unaligned' ||
      alignmentOption === 'To be unaligned') &&
    isStatusChanged;

  return (
    <>
      <SelectFormField
        name='new_status'
        label={'Program status'}
        disabled={disabled}
        options={programStatusOptions.map(o => ({
          value: o,
          label: o,
        }))}
      />
      {isNewStatusAligned && (
        <>
          {tierOptions.length > 0 && (
            <SelectFormField
              name='tier'
              label={'Tier'}
              disabled={disabled}
              options={tierOptions.map(o => ({
                value: o,
                label: o,
              }))}
              optional={tierOptions.length === 0}
            />
          )}
          {programName === 'GUIDE' && (
            <>
              <Typography
                variant='caption'
                sx={{ px: 1, mt: '-16px', color: 'text.disabled' }}
              >
                {t('patient.detailsCard.editPatientProgram.respiteInfo')}
              </Typography>
              <Typography variant='body1' component='h2'>
                {t(
                  'patient.detailsCard.editPatientProgram.respiteServicesLabel',
                )}
              </Typography>
              <Controller
                name={'respite_eligible'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl sx={{ width: '100%' }}>
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        key={'Unknown option'}
                        value={'Unknown'}
                        control={<Radio value={'Unknown'} />}
                        label={t(
                          'patient.detailsCard.editPatientProgram.unknown',
                        )}
                      />
                      <FormControlLabel
                        key={'Eligible option'}
                        value={'Eligible'}
                        control={<Radio value={'Eligible'} />}
                        label={t(
                          'patient.detailsCard.editPatientProgram.eligible',
                        )}
                      />
                      <FormControlLabel
                        key={'Ineligible option'}
                        value={'Ineligible'}
                        control={<Radio value={'Ineligible'} />}
                        label={t(
                          'patient.detailsCard.editPatientProgram.ineligible',
                        )}
                      />
                    </RadioGroup>
                    <FormHelperText error={!isNil(error)}>
                      {error ? error.message : null}
                    </FormHelperText>
                  </FormControl>
                )}
              />
              {respite_eligible === 'Ineligible' && (
                <SelectFormField
                  name='respite_ineligibility_reason'
                  label={'Ineligibility reason'}
                  options={ineligibilityReasons.map(o => ({
                    value: o,
                    label: o,
                  }))}
                  optional={false}
                />
              )}
            </>
          )}
          <DatepickerFormField
            name='alignment_date'
            label={'Alignment date'}
            optional={false}
            disableFuture={false}
            disabled={disabled}
          />
          <DatepickerFormField
            name='alignment_notification_date'
            label={'Alignment notification date'}
            optional={false}
            disabled={disabled}
          />
        </>
      )}
      {isNewStatusRejected && (
        <>
          <TextFormField
            name='rejection_reason'
            label={'Rejection reason'}
            disabled={disabled}
          />
        </>
      )}
      {isNewStatusUnaligned && (
        <>
          <SelectFormField
            name='unalignment_reason'
            label={'Unalignment reason'}
            disabled={disabled}
            options={unalignmentReasons.map(o => ({
              value: o,
              label: o,
            }))}
          />
          <DatepickerFormField
            name='unalignment_date'
            label={'Unalignment date'}
            disabled={disabled}
            optional={false}
          />
        </>
      )}
    </>
  );
};
